"use client";

import React, { useState } from 'react';
// 1. Import motion for animations and sonner for toast notifications
import { motion } from 'framer-motion';
import { Toaster, toast } from 'sonner';

// 2. Reusable animation variants (identical to the contact page)
const fadeInUp = {
  initial: { opacity: 0, y: 40 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  whileInView: { opacity: 1, y: 0, transition: { duration: 0.6, ease: "easeOut" } },
  viewport: { once: true, amount: 0.2 }
};

const staggerContainer = {
  initial: {},
  animate: {
    transition: {
      staggerChildren: 0.1,
    },
  },
  whileInView: {
    transition: {
      staggerChildren: 0.1,
    },
  },
  viewport: { once: true, amount: 0.2 }
};

const slideIn = (direction = 'left') => ({
  initial: {
    x: direction === 'left' ? -100 : 100,
    opacity: 0,
  },
  whileInView: {
    x: 0,
    opacity: 1,
    transition: {
      type: 'spring',
      stiffness: 100,
      damping: 20,
    },
  },
  viewport: { once: true, amount: 0.2 }
});

const sentenceVariant = {
  animate: {
    transition: {
      delayChildren: 0.3,
      staggerChildren: 0.08, // slightly faster for a snappy feel
    },
  },
};

const wordVariant = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 100 } },
};

export default function RequestDemo() {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    preferredDate: '',
    preferredTime: '',
    comments: '',
  });
  // The old 'status' state is no longer needed
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // 3. Update handleSubmit to use sonner toast notifications with a real API call
  const handleSubmit = (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    // Create a promise from the fetch call to the backend API
    const promise = fetch("/api/request-demo", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    }).then(res => {
        // Check if the response was successful. If not, throw an error to be caught by toast.promise
        if (!res.ok) {
            // We can try to get more specific error from backend if available
            throw new Error("Failed to submit request. Please try again.");
        }
        // If successful, parse the JSON response (even if it's just a simple success status)
        return res.json();
    });

    toast.promise(promise, {
      loading: 'Submitting your request...',
      success: (data) => {
        // On success, reset the form and submission state
        setFormData({ name: '', company: '', email: '', phone: '', preferredDate: '', preferredTime: '', comments: '' });
        setIsSubmitting(false);
        // Return the static, user-friendly success message that was there before.
        return "Thank you! Your demo request has been received. We will contact you shortly.";
      },
      error: (err) => {
        // On error, just update the submission state
        setIsSubmitting(false);
        // Return the error message to be displayed in the toast
        return err.message || "Submission failed. Please try again later.";
      },
    });
  };

  return (
    <main className="font-sans bg-white text-gray-900 min-h-screen scroll-smooth overflow-x-hidden">
      {/* 4. Add the Toaster component, which will render all notifications */}
      <Toaster richColors position="bottom-right" />

      {/* Hero Section */}
      <section className="relative flex min-h-[60vh] items-center justify-center px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-gray-50 to-white overflow-hidden">
        <div className="absolute inset-0 z-0 overflow-hidden opacity-20">
          <video autoPlay loop muted playsInline className="w-full h-full object-cover">
            <source src="/images/Video2.mp4" type="video/mp4" />
          </video>
        </div>
        <div className="absolute inset-0 z-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 1400 400" preserveAspectRatio="xMidYMid slice">
            <path fill="#16a085" d="M0,400L80,373.3C160,347,320,293,480,280C640,267,800,293,960,306.7C1120,320,1280,320,1360,320L1400,320L1400,0L1360,0C1280,0,1120,0,960,0C800,0,640,0,480,0C320,0,160,0,80,0L0,0Z" className="animate-pulse-slow" />
            <circle cx="700" cy="200" r="120" fill="#16a085" fillOpacity="0.2" className="animate-pulse" />
          </svg>
        </div>
        
        <motion.div
          className="relative z-10 max-w-7xl mx-auto text-center"
          style={{ maxWidth: '1400px' }}
          variants={staggerContainer}
          initial="initial"
          animate="animate"
        >
          <motion.h1 
            className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-gray-900 mb-6 tracking-tight"
            variants={sentenceVariant}
          >
            <span className="block">
              {"Experience Void Firsthand".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>{word} </motion.span>
              ))}
            </span>
            <span className="block text-[#16a085] mt-2">
              {"Request Your Personalized Demo".split(" ").map((word, index) => (
                <motion.span key={index} className="inline-block" variants={wordVariant}>{word} </motion.span>
              ))}
            </span>
          </motion.h1>
          <motion.p variants={fadeInUp} className="mt-4 max-w-3xl mx-auto text-lg sm:text-xl text-gray-600 leading-relaxed">
            Join our family and discover how Void’s intuitive platform can elevate your customer support to new heights. Let’s explore together!
          </motion.p>
          <motion.div variants={fadeInUp} className="mt-10">
            <motion.a
              href="#demo-form"
              className="inline-block bg-[#16a085] text-white font-semibold px-8 py-3 rounded-lg shadow-lg"
              whileHover={{ y: -5, scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              transition={{ type: 'spring', stiffness: 300 }}
            >
              Schedule Your Demo
            </motion.a>
          </motion.div>
        </motion.div>
      </section>

      {/* Why Request a Demo */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 {...fadeInUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            Why Experience Void?
          </motion.h2>
          <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true, amount: 0.2 }} className="grid md:grid-cols-3 gap-8">
            {[
              { title: "Tailored to You", description: "Our team will customize the demo to address your unique business needs, ensuring you see Void’s full potential.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg> },
              { title: "Hands-On Exploration", description: "Interact with Void’s powerful features, from AI-driven chat to real-time dashboards, guided by our friendly experts.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg> },
              { title: "No Pressure, Just Support", description: "We’re here to answer your questions and show you how Void fits into your vision, with no obligation.", icon: <svg className="w-12 h-12 text-[#16a085]" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg> },
            ].map((item, index) => (
              <motion.div key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
                <div className="flex items-center mb-4">{item.icon}</div>
                <h3 className="text-xl font-semibold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-gray-600">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Demo Request Form */}
      <section id="demo-form" className="py-20 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-12 items-center" style={{ maxWidth: '1400px' }}>
          
          <motion.div {...slideIn('left')}>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-8">Let’s Get Started</h2>
            <p className="text-lg text-gray-600 mb-6 leading-relaxed">Fill out the form below, and a member of our Void family will reach out to schedule your personalized demo. We can’t wait to show you what’s possible!</p>
            <motion.form variants={staggerContainer} initial="initial" whileInView="whileInView" onSubmit={handleSubmit} className="bg-white rounded-xl p-8 shadow-lg border border-gray-100" noValidate>
              <motion.div variants={fadeInUp} className="mb-6">
                <label htmlFor="name" className="block mb-2 font-semibold text-gray-800">Full Name <span className="text-red-600">*</span></label>
                <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} placeholder="Your full name" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
              </motion.div>
              <motion.div variants={fadeInUp} className="mb-6">
                 <label htmlFor="company" className="block mb-2 font-semibold text-gray-800">Company / Organization</label>
                 <input type="text" id="company" name="company" value={formData.company} onChange={handleChange} placeholder="Your company or organization" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
              </motion.div>
              <motion.div variants={fadeInUp} className="mb-6">
                <label htmlFor="email" className="block mb-2 font-semibold text-gray-800">Email <span className="text-red-600">*</span></label>
                <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} placeholder="you@example.com" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
              </motion.div>
              <motion.div variants={fadeInUp} className="mb-6">
                 <label htmlFor="phone" className="block mb-2 font-semibold text-gray-800">Phone Number</label>
                 <input type="tel" id="phone" name="phone" value={formData.phone} onChange={handleChange} placeholder="+1 (234) 567-890" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
              </motion.div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-6">
                <motion.div variants={fadeInUp}>
                  <label htmlFor="preferredDate" className="block mb-2 font-semibold text-gray-800">Preferred Demo Date</label>
                  <input type="date" id="preferredDate" name="preferredDate" value={formData.preferredDate} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
                </motion.div>
                <motion.div variants={fadeInUp}>
                  <label htmlFor="preferredTime" className="block mb-2 font-semibold text-gray-800">Preferred Demo Time</label>
                  <input type="time" id="preferredTime" name="preferredTime" value={formData.preferredTime} onChange={handleChange} className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300" />
                </motion.div>
              </div>
              <motion.div variants={fadeInUp} className="mb-6">
                <label htmlFor="comments" className="block mb-2 font-semibold text-gray-800">Additional Comments</label>
                <textarea id="comments" name="comments" rows="5" value={formData.comments} onChange={handleChange} placeholder="Any specific needs or questions?" className="w-full px-4 py-3 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-[#16a085] transition-all duration-300"></textarea>
              </motion.div>
              <motion.button variants={fadeInUp} type="submit" disabled={isSubmitting} className="bg-[#16a085] text-white font-semibold px-8 py-4 rounded-lg shadow-lg w-full disabled:opacity-50 disabled:cursor-not-allowed" whileHover={{ scale: 1.02, y: -2 }} whileTap={{ scale: 0.98 }}>
                {isSubmitting ? "Submitting..." : "Submit Demo Request"}
              </motion.button>
            </motion.form>
          </motion.div>
          
          <motion.div {...slideIn('right')} className="relative">
            <img src="/images/LetsGetStarted-01.png" alt="Demo Illustration" className="rounded-xl shadow-lg w-full transform hover:scale-[1.02] transition-transform duration-300" />
            <div className="absolute inset-0 -z-10 opacity-20">
              <svg className="w-full h-full" viewBox="0 0 600 400">
                <path d="M100 300 Q300 100 500 300" stroke="#16a085" strokeWidth="4" fill="none" className="animate-pulse-slow" />
                <circle cx="300" cy="100" r="20" fill="#16a085" className="animate-pulse" />
                <circle cx="100" cy="300" r="20" fill="#16a085" className="animate-pulse" />
                <circle cx="500" cy="300" r="20" fill="#16a085" className="animate-pulse" />
              </svg>
            </div>
            <motion.div variants={fadeInUp} className="absolute -bottom-6 -right-6 bg-white p-4 rounded-lg shadow-lg border border-gray-100">
              <div className="flex items-center gap-2">
                <div className="text-3xl font-bold text-[#16a085]">100%</div>
                <div className="text-sm font-medium text-gray-600">Satisfaction<br />Guarantee</div>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-7xl mx-auto" style={{ maxWidth: '1400px' }}>
          <motion.h2 {...fadeInUp} className="text-3xl sm:text-4xl font-extrabold text-gray-900 mb-12 text-center tracking-tight">
            What Our Partners Say
          </motion.h2>
          <div className="relative">
            <div className="absolute inset-0 -z-10 opacity-10">
              <svg className="w-full h-full" viewBox="0 0 1400 400">
                <circle cx="700" cy="200" r="200" fill="#16a085" fillOpacity="0.3" />
                <path d="M200 300 Q700 100 1200 300" stroke="#16a085" strokeWidth="6" fill="none" className="animate-pulse-slow" />
              </svg>
            </div>
            <motion.div variants={staggerContainer} initial="initial" whileInView="whileInView" viewport={{ once: true, amount: 0.2 }} className="grid md:grid-cols-2 gap-8">
              {[
                { quote: "The Void demo was a game-changer. Their team made us feel like family, walking us through every feature with patience and expertise.", author: "E-qera.xyz, CEO of TechTrend Innovations" },
                { quote: "Seeing Void in action gave us confidence in its ability to streamline our support. The personalized demo was professional and inspiring!", author: "Dark-tech.xyz, Head of Customer Success at GrowEasy" }
              ].map((item, index) => (
                <motion.div key={index} variants={fadeInUp} className="bg-white p-6 rounded-xl shadow-lg border border-gray-100 hover:shadow-xl transition-all duration-300">
                  <div className="flex items-start mb-4">
                    <svg className="w-8 h-8 text-[#16a085]" fill="currentColor" viewBox="0 0 20 20"><path d="M10 2a2 2 0 00-2 2v1H7a2 2 0 00-2 2v7a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-1V4a2 2 0 00-2-2zM9 4h2v1H9V4z"></path></svg>
                  </div>
                  <p className="text-gray-600 italic mb-4">"{item.quote}"</p>
                  <p className="text-gray-900 font-semibold">{item.author}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}